import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import Header from "../../components/header/Header";
import Footer from "../../components/footer/Footer";
import Button from "../../components/button/Button";
import TopButton from "../../components/topButton/TopButton";
import ProjectLanguages from "../../components/projectLanguages/ProjectLanguages";
import { Fade } from "react-reveal";
import ProjectsData from "../../shared/opensource/projects.json";
import "./ProjectDetail.css";

class ProjectDetail extends Component {
  constructor(props) {
    super(props);
    const projectId = this.props.match.params.projectId;
    const project = ProjectsData.data.find(p => p.id === projectId);
    
    this.state = {
      projectId: projectId,
      project: project,
      selectedImage: 0,
      lightboxOpen: false
    };
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    document.addEventListener('keydown', this.handleKeyPress);
    
    if (!this.state.project) {
      this.props.history.push('/projects');
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyPress);
  }

  selectImage = (index) => {
    this.setState({ selectedImage: index });
  };

  openLightbox = (index) => {
    this.setState({ 
      lightboxOpen: true,
      selectedImage: index 
    });
  };

  closeLightbox = () => {
    this.setState({ lightboxOpen: false });
  };

  nextImage = () => {
    const { project, selectedImage } = this.state;
    this.setState({
      selectedImage: (selectedImage + 1) % project.gallery.length
    });
  };

  prevImage = () => {
    const { project, selectedImage } = this.state;
    this.setState({
      selectedImage: selectedImage === 0 
        ? project.gallery.length - 1 
        : selectedImage - 1
    });
  };

  handleKeyPress = (e) => {
    if (!this.state.lightboxOpen) return;
    
    if (e.key === 'Escape') {
      this.closeLightbox();
    } else if (e.key === 'ArrowLeft') {
      this.prevImage();
    } else if (e.key === 'ArrowRight') {
      this.nextImage();
    }
  };

  getProjectBadge = (url) => {
    if (url === "private") {
      return { text: "Private Project", color: "#dc3545", bgColor: "#f8d7da" };
    } else {
      return { text: "Public Project", color: "#28a745", bgColor: "#d4edda" };
    }
  };

  render() {
    const theme = this.props.theme;
    const { project, selectedImage, lightboxOpen } = this.state;

    if (!project) {
      return null;
    }

    const badge = this.getProjectBadge(project.url);

    return (
      <div className="project-detail-main">
        <Header theme={theme} />
        
        {/* Back Button */}
        <Fade bottom duration={1000} distance="20px">
          <div className="back-button-container">
            <button 
              className="back-button"
              onClick={() => this.props.history.push('/projects')}
              style={{ color: theme.text }}
            >
              ← Back to Projects
            </button>
          </div>
        </Fade>

        {/* Project Header Section */}
        <Fade bottom duration={1200} distance="40px">
          <div className="project-detail-header">
            <div className="project-detail-heading-div">
              <div className="project-title-container">
                <h1 
                  className="project-detail-title"
                  style={{ color: theme.text }}
                >
                  {project.name}
                </h1>
                <span 
                  className="project-badge"
                  style={{ 
                    backgroundColor: badge.bgColor,
                    color: badge.color
                  }}
                >
                  {badge.text}
                </span>
              </div>
              
              <p 
                className="project-detail-subtitle subTitle"
                style={{ color: theme.secondaryText }}
              >
                {project.shortDescription || project.description}
              </p>
              
              {/* Project Meta Info */}
              <div className="project-meta-info">
                {project.category && (
                  <div className="meta-item">
                    <span className="meta-label" style={{ color: theme.secondaryText }}>Category:</span>
                    <span className="meta-value" style={{ color: theme.text }}>{project.category}</span>
                  </div>
                )}
                {project.status && (
                  <div className="meta-item">
                    <span className="meta-label" style={{ color: theme.secondaryText }}>Status:</span>
                    <span className="meta-value status-active" style={{ color: '#10b981' }}>{project.status}</span>
                  </div>
                )}
                <div className="meta-item">
                  <span className="meta-label" style={{ color: theme.secondaryText }}>Created:</span>
                  <span className="meta-value" style={{ color: theme.text }}>{project.createdAt.split("T")[0]}</span>
                </div>
              </div>
            </div>
          </div>
        </Fade>

        {/* Project Images Gallery */}
        {project.gallery && project.gallery.length > 0 && (
          <Fade bottom duration={1400} distance="40px">
            <div className="project-images-section">
              <div 
                className="project-main-image-wrapper"
                onClick={() => this.openLightbox(selectedImage)}
              >
                <img 
                  src={require(`../../assets/images/${project.gallery[selectedImage]}`)}
                  alt={`${project.name} - View ${selectedImage + 1}`}
                  className="project-main-image"
                />
                <div className="image-zoom-overlay">
                  <span className="zoom-text" role="img" aria-label="zoom">🔍 Click to expand</span>
                </div>
              </div>

              <div className="project-thumbnails-gallery">
                {project.gallery.map((image, index) => (
                  <div
                    key={index}
                    className={`project-thumbnail ${selectedImage === index ? 'active' : ''}`}
                    onClick={() => this.selectImage(index)}
                    style={{
                      borderColor: selectedImage === index ? theme.text : 'transparent'
                    }}
                  >
                    <img
                      src={require(`../../assets/images/${image}`)}
                      alt={`Thumbnail ${index + 1}`}
                    />
                    {selectedImage === index && (
                      <div className="thumbnail-active-indicator"></div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </Fade>
        )}

        {/* Full Description */}
        {project.fullDescription && (
          <Fade bottom duration={1500} distance="40px">
            <div className="project-full-description-section">
              <div className="content-card" style={{ backgroundColor: theme.highlight }}>
                <h2 className="section-title" style={{ color: theme.text }}>
                  About This Project
                </h2>
                {project.fullDescription.split('\n\n').map((paragraph, index) => (
                  <p key={index} className="description-paragraph" style={{ color: theme.secondaryText }}>
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
          </Fade>
        )}

        {/* Highlights */}
        {project.highlights && project.highlights.length > 0 && (
          <Fade bottom duration={1550} distance="40px">
            <div className="project-highlights-section">
              <h2 className="section-title" style={{ color: theme.text }}>
                Key Highlights
              </h2>
              <div className="highlights-grid">
                {project.highlights.map((highlight, index) => (
                  <div 
                    key={index} 
                    className="highlight-card"
                    style={{ 
                      backgroundColor: theme.highlight,
                      borderLeft: `4px solid ${theme.text}`
                    }}
                  >
                    <h3 className="highlight-title" style={{ color: theme.text }}>
                      {highlight.title}
                    </h3>
                    <p className="highlight-description" style={{ color: theme.secondaryText }}>
                      {highlight.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </Fade>
        )}

        {/* Features */}
        {project.features && (
          <Fade bottom duration={1600} distance="40px">
            <div className="project-features-section">
              <h2 className="section-title" style={{ color: theme.text }}>
                Key Features
              </h2>
              <div className="features-grid">
                {project.features.map((feature, index) => (
                  <div 
                    key={index} 
                    className="feature-item"
                    style={{ 
                      backgroundColor: theme.highlight,
                      color: theme.text,
                      border: `1px solid ${theme.text}10`
                    }}
                  >
                    <span className="feature-check">✓</span>
                    {feature}
                  </div>
                ))}
              </div>
            </div>
          </Fade>
        )}

        {/* Technologies */}
        {project.languages && (
          <Fade bottom duration={1700} distance="40px">
            <div className="project-technologies-section">
              <h2 className="section-title" style={{ color: theme.text }}>
                Technologies Used
              </h2>
              <div className="technologies-icons">
                <ProjectLanguages logos={project.languages} />
              </div>
              {project.technologies && (
                <div className="technologies-details">
                  {project.technologies.map((tech, index) => (
                    <span 
                      key={index} 
                      className="technology-tag"
                      style={{ 
                        background: `linear-gradient(135deg, ${theme.highlight || '#f8f9fa'}, ${theme.body || '#ffffff'})`,
                        color: theme.text,
                        border: `2px solid ${theme.text}20`
                      }}
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </Fade>
        )}

        {/* Metrics */}
        {project.metrics && (
          <Fade bottom duration={1800} distance="40px">
            <div className="project-metrics-section">
              <h2 className="section-title" style={{ color: theme.text }}>
                Impact & Results
              </h2>
              <div className="metrics-grid">
                {Object.entries(project.metrics).map(([key, value], index) => (
                  <div 
                    key={index} 
                    className="metric-card"
                    style={{ backgroundColor: theme.highlight }}
                  >
                    <div className="metric-value" style={{ color: theme.text }}>
                      {value}
                    </div>
                    <div className="metric-label" style={{ color: theme.secondaryText }}>
                      {key.charAt(0).toUpperCase() + key.slice(1)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Fade>
        )}

        {/* Testimonial */}
        {project.testimonial && (
          <Fade bottom duration={1900} distance="40px">
            <div className="project-testimonial-section">
              <div className="testimonial-card" style={{ backgroundColor: theme.highlight }}>
                <div className="quote-icon" style={{ color: theme.text }}>❝</div>
                <p className="testimonial-text" style={{ color: theme.text }}>
                  {project.testimonial.text}
                </p>
                <div className="testimonial-author">
                  <p className="author-name" style={{ color: theme.text }}>
                    {project.testimonial.author}
                  </p>
                  <p className="author-position" style={{ color: theme.secondaryText }}>
                    {project.testimonial.position}
                  </p>
                </div>
              </div>
            </div>
          </Fade>
        )}

        {/* Project Links */}
        <Fade bottom duration={2000} distance="40px">
          <div className="project-links-section">
            <div className="project-buttons">
              {project.url && project.url !== "private" && (
                <Button
                  text={"Visit Website →"}
                  className="project-button"
                  href={project.url}
                  newTab={true}
                  theme={theme}
                />
              )}
              {project.demoUrl && (
                <Button
                  text={"Try Demo"}
                  className="project-button"
                  href={project.demoUrl}
                  newTab={true}
                  theme={theme}
                />
              )}
            </div>
          </div>
        </Fade>

        {/* Lightbox Modal */}
        {lightboxOpen && (
          <div className="lightbox-modal" onClick={this.closeLightbox}>
            <button 
              className="lightbox-close"
              onClick={this.closeLightbox}
              aria-label="Close"
            >
              ×
            </button>
            
            <button 
              className="lightbox-nav lightbox-prev"
              onClick={(e) => {
                e.stopPropagation();
                this.prevImage();
              }}
              aria-label="Previous image"
            >
              ‹
            </button>
            
            <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
              <img
                src={require(`../../assets/images/${project.gallery[selectedImage]}`)}
                alt={`${project.name} - Full view`}
              />
              <p className="lightbox-counter">
                {selectedImage + 1} / {project.gallery.length}
              </p>
            </div>
            
            <button 
              className="lightbox-nav lightbox-next"
              onClick={(e) => {
                e.stopPropagation();
                this.nextImage();
              }}
              aria-label="Next image"
            >
              ›
            </button>
          </div>
        )}

        <Footer theme={this.props.theme} onToggle={this.props.onToggle} />
        <TopButton theme={this.props.theme} />
      </div>
    );
  }
}

export default withRouter(ProjectDetail);